/**
 * Description:
 * Integrates Google reCAPTCHA v2 with Login and Register forms
 * to prevent bot account creation and login attempts.
 *
 * @module RecaptchaIntegration
 */

define(
	'RecaptchaIntegration',
	[
		'LoginRegister.Login.View',
		'LoginRegister.Register.View',
		'SC.Configuration',
		'underscore',
		'jQuery',
		'Backbone'
	],
	function (
		LoginRegisterLoginView,
		LoginRegisterRegisterView,
		Configuration,
		_,
		jQuery,
		Backbone
	) {
		'use strict';

		return {
			// These will be read from NetSuite Configuration
			SITE_KEY: null,
			ENABLED: null,

			// Track if reCAPTCHA script is loaded
			scriptLoaded: false,

			widgetIds: {},

			/**
			 * Check if reCAPTCHA is enabled (can be called from other extensions)
			 */
			isEnabled: function() {
				return this.ENABLED === true;
			},

			mountToApp: function (container) {
				console.log('reCAPTCHA Integration: Initialized');

				// Read configuration from NetSuite
				this.ENABLED = Configuration.get('RecaptchaIntegration.enabled', true);
				this.SITE_KEY = Configuration.get('RecaptchaIntegration.siteKey', '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI');

				console.log('[Recaptcha] Configuration loaded - Enabled:', this.ENABLED, 'Site Key:', this.SITE_KEY ? '(configured)' : '(missing)');

				// Check if disabled via NetSuite configuration
				if (!this.ENABLED) {
					console.warn('[Recaptcha] reCAPTCHA is DISABLED via NetSuite configuration');
					console.warn('[Recaptcha] To enable: Go to NetSuite > Customization > SuiteCommerce Advanced > Configuration > Extensions > reCAPTCHA Protection');
					// Still expose globally so other code can check status
					window.RecaptchaIntegration = this;
					return;
				}

				if (!this.SITE_KEY) {
					console.error('[Recaptcha] Site Key is not configured! Please set it in NetSuite Configuration.');
					window.RecaptchaIntegration = this;
					return;
				}

				console.log('[Recaptcha] reCAPTCHA is ENABLED and will protect login/register forms');

				// Load reCAPTCHA script
				this.loadRecaptchaScript();

				// Extend Login View
				this.extendLoginView();

				// Extend Register View
				this.extendRegisterView();

				// Expose globally so other extensions (like LeadRegistrationFormReconfig) can access widget IDs
    			window.RecaptchaIntegration = this;
			},

			/**
			 * Load Google reCAPTCHA script
			 */
			loadRecaptchaScript: function () {
				var self = this;

				if (!this.scriptLoaded && !window.grecaptcha) {
					console.log('[Recaptcha] Loading Google reCAPTCHA script...');
					var script = document.createElement('script');
					script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaApiLoaded&render=explicit';
					script.async = true;
					script.defer = true;

					// Define a global callback for when reCAPTCHA API finishes loading
					window.onRecaptchaApiLoaded = function () {
						self.scriptLoaded = true;
						console.log('[Recaptcha] reCAPTCHA API loaded and ready');

						// Now render both widgets safely
						if (document.querySelector('.login-register-login-form')) {
							self.addRecaptchaToLogin();
						}
						if (document.querySelector('.login-register-register-form')) {
							self.addRecaptchaToRegister();
						}
					};

					document.head.appendChild(script);
				} else if (window.grecaptcha && window.grecaptcha.render) {
					console.log('[Recaptcha] reCAPTCHA already loaded');
					self.scriptLoaded = true;
				}
			},


			/**
			 * Extend Login View to include reCAPTCHA
			 */
			extendLoginView: function () {
				var self = this;

				// Store original render method
				var originalRender = LoginRegisterLoginView.prototype.render;

				// Override render method
				LoginRegisterLoginView.prototype.render = function () {
					originalRender.apply(this, arguments);

					// Add reCAPTCHA container after form renders
					setTimeout(function() {
						self.addRecaptchaToLogin();
					}, 100);

					return this;
				};

				// Add listener to login button click since some templates bypass submit()
				jQuery(document).on('click', '.login-register-login-form [data-type="form-login-action"]', function (e) {
					console.log('[Recaptcha] Login button clicked');

					var $form = jQuery('.login-register-login-form');
					var recaptchaResponse = '';
					if (typeof grecaptcha !== 'undefined' && grecaptcha.getResponse) {
						if (window.RecaptchaIntegration && RecaptchaIntegration.widgetIds) {
							var widgetId = RecaptchaIntegration.widgetIds['login-recaptcha'];
							recaptchaResponse = grecaptcha.getResponse(widgetId);
							console.log('[Recaptcha] Using widget ID', widgetId, '→', recaptchaResponse);
						} else {
							recaptchaResponse = grecaptcha.getResponse();
							console.log('[Recaptcha] Using fallback global getResponse →', recaptchaResponse);
						}
					}

					// If user hasn't completed the reCAPTCHA, block submission
					if (!recaptchaResponse) {
						e.preventDefault();
						console.warn('[Recaptcha] Login blocked: reCAPTCHA not completed');

						// Find the login form’s alert placeholder
						var $alertContainer = jQuery('.login-register-login-form [data-type="alert-placeholder"]');

						if ($alertContainer.length) {
							$alertContainer.html(
								'<div class="alert alert-error" role="alert" style="background-color:#fdf0f2;border:1px solid #c01a29;color:#c01a29;padding:10px;margin:10px 0;">' +
								'<strong>Please complete the reCAPTCHA verification before logging in.</strong>' +
								'</div>'
							);
						} else {
							alert('Please complete the reCAPTCHA verification before logging in.');
						}

						return false;
					}

					// Inject hidden input with token before submission
					$form.find('[name="g-recaptcha-response"]').remove();
					$form.append(
						jQuery('<input>')
							.attr('type', 'hidden')
							.attr('name', 'g-recaptcha-response')
							.val(recaptchaResponse)
					);

					console.log('[Recaptcha] reCAPTCHA verified — proceeding with login');
				});

				// Store original submit method
				var originalSubmit = LoginRegisterLoginView.prototype.submit;

				// Override submit to validate reCAPTCHA
				LoginRegisterLoginView.prototype.submit = function (e) {
					console.log('[Recaptcha] Login form submit triggered');

					if (e) {
						e.preventDefault();
					}

					var recaptchaResponse = self.getRecaptchaResponse('login-recaptcha');
					console.log('[Recaptcha] Login reCAPTCHA response →', recaptchaResponse);

					if (!recaptchaResponse) {
						// Show error message
						self.showRecaptchaError(this, 'Please complete the reCAPTCHA verification.');
						return false;
					}

					// Add reCAPTCHA response to form data
					this.$('[name="g-recaptcha-response"]').remove();
					this.$('form').append(
						jQuery('<input>')
							.attr('type', 'hidden')
							.attr('name', 'g-recaptcha-response')
							.val(recaptchaResponse)
					);

					// Call original submit and monitor for failures
					var result = originalSubmit.apply(this, arguments);

					// Monitor for error messages (form validation failures)
					// reCAPTCHA tokens are single-use, so reset if submission fails
					setTimeout(function() {
						var $alertPlaceholder = jQuery('.login-register-login-form [data-type="alert-placeholder"]');
						if ($alertPlaceholder.length && $alertPlaceholder.find('.alert-error').length > 0) {
							console.log('[Recaptcha] Login submission failed, resetting reCAPTCHA');
							self.resetRecaptcha('login-recaptcha');
						}
					}, 500);

					return result;
				};
			},

			/**
			 * Extend Register View to include reCAPTCHA
			 */
			extendRegisterView: function () {
				var self = this;

				// Store original render method
				var originalRender = LoginRegisterRegisterView.prototype.render;

				// Override render method
				LoginRegisterRegisterView.prototype.render = function () {
					originalRender.apply(this, arguments);

					// Add reCAPTCHA container after form renders
					setTimeout(function() {
						self.addRecaptchaToRegister();
					}, 100);

					return this;
				};

				// Store original submit method
				var originalSubmit = LoginRegisterRegisterView.prototype.submit;

				// Override submit to validate reCAPTCHA
				LoginRegisterRegisterView.prototype.submit = function (e) {
					if (e) {
						e.preventDefault();
					}

					var recaptchaResponse = self.getRecaptchaResponse('register-recaptcha');

					if (!recaptchaResponse) {
						// Show error message
						self.showRecaptchaError(this, 'Please complete the reCAPTCHA verification before registering.');
						return false;
					}

					// Add reCAPTCHA response to form data
					this.$('[name="g-recaptcha-response"]').remove();
					this.$('form').append(
						jQuery('<input>')
							.attr('type', 'hidden')
							.attr('name', 'g-recaptcha-response')
							.val(recaptchaResponse)
					);

					// Call original submit and monitor for failures
					var result = originalSubmit.apply(this, arguments);

					// Monitor for error messages (form validation failures)
					// reCAPTCHA tokens are single-use, so reset if submission fails
					setTimeout(function() {
						var $alertPlaceholder = jQuery('.login-register-register-form [data-type="alert-placeholder"]');
						if ($alertPlaceholder.length && $alertPlaceholder.find('.alert-error').length > 0) {
							console.log('[Recaptcha] Registration submission failed, resetting reCAPTCHA');
							self.resetRecaptcha('register-recaptcha');
						}
					}, 500);

					return result;
				};
			},

			/**
			 * Add reCAPTCHA to login form
			 */
			addRecaptchaToLogin: function () {
				var loginForm = jQuery('.login-register-login-form');
				var existingContainer = jQuery('#login-recaptcha');

				// If container exists but is empty, remove it so we can re-create it
				if (existingContainer.length && existingContainer.children().length === 0) {
					console.log('[Recaptcha] Found empty login-recaptcha container, removing...');
					existingContainer.remove();
					delete this.widgetIds['login-recaptcha'];
				}

				if (loginForm.length && !jQuery('#login-recaptcha').length) {
					var recaptchaContainer = jQuery('<div>')
						.attr('id', 'login-recaptcha')
						.addClass('recaptcha-container')
						.css({
							'margin': '15px 0',
						});

					// Insert before submit button
					loginForm.find('[data-type="form-login-action"]').before(recaptchaContainer);

					// Render reCAPTCHA
					this.renderRecaptcha('login-recaptcha');
				}
			},

			/**
			 * Add reCAPTCHA to register form
			 */
			addRecaptchaToRegister: function () {
				var registerForm = jQuery('.login-register-register-form');
				var existingContainer = jQuery('#register-recaptcha');

				// If container exists but is empty, remove it so we can re-create it
				if (existingContainer.length && existingContainer.children().length === 0) {
					console.log('[Recaptcha] Found empty register-recaptcha container, removing...');
					existingContainer.remove();
					delete this.widgetIds['register-recaptcha'];
				}

				if (registerForm.length && !jQuery('#register-recaptcha').length) {
					var recaptchaContainer = jQuery('<div>')
						.attr('id', 'register-recaptcha')
						.addClass('recaptcha-container')
						.css({
							'margin': '15px 0',
							'clear': 'both'
						});

					// Insert before the hidden submit button
					registerForm.find('#submission-button').parent().before(recaptchaContainer);

					// Render reCAPTCHA
					this.renderRecaptcha('register-recaptcha');
				}
			},

			/**
			 * Render reCAPTCHA widget
			 */
			renderRecaptcha: function (containerId) {
				var self = this;

				var renderWidget = function () {
					if (typeof window.grecaptcha !== 'undefined' && typeof window.grecaptcha.render === 'function') {
						try {
							window.grecaptcha.ready(function () {
								var el = document.getElementById(containerId);
								if (!el) {
									console.warn('[Recaptcha] render: missing container', containerId);
									return;
								}

								// IMPORTANT: trust our tracked widgetIds, not DOM child nodes.
								if (self.widgetIds[containerId] === undefined) {
									// Clear any stray children left by a view re-render
									try { el.innerHTML = ''; } catch (_) {}

									var widgetId = window.grecaptcha.render(containerId, { 'sitekey': self.SITE_KEY });
									self.widgetIds[containerId] = widgetId;
									console.log('[Recaptcha] Rendered', containerId, 'with widget ID', widgetId);
								} else {
									console.log('[Recaptcha] Widget already tracked for', containerId);
								}
							});
						} catch (e) {
							console.error('[Recaptcha] render error:', e);
						}
					} else {
						console.log('[Recaptcha] Waiting for grecaptcha to load...');
						setTimeout(renderWidget, 300);
					}
				};

				renderWidget();
			},


			/**
			 * Get reCAPTCHA response
			 */
			getRecaptchaResponse: function (containerId) {
				var self = this;
				var widgetId = self.widgetIds[containerId];

				if (typeof window.grecaptcha === 'undefined' || typeof window.grecaptcha.getResponse !== 'function') {
					console.warn('[Recaptcha] grecaptcha not ready for getResponse');
					return '';
				}

				try {
					if (widgetId === undefined) {
						console.warn('[Recaptcha] No widget ID found for', containerId);
						return '';
					}
					var token = window.grecaptcha.getResponse(widgetId);
					console.log('[Recaptcha] getResponse for', containerId, '→', token);
					return token || '';
				} catch (err) {
					// Most common: "reCAPTCHA client element has been removed"
					console.warn('[Recaptcha] Error getting response for widget', widgetId, ':', err);
					console.warn('[Recaptcha] Widget may have been removed. Clearing widget ID.');
					delete self.widgetIds[containerId];
					try {
						var el = self.__freshContainer(containerId) || document.getElementById(containerId);
						if (el) { try { el.innerHTML = ''; } catch(_) {} }
						setTimeout(function(){ self.renderRecaptcha(containerId); }, 0);
					} catch (e2) {
						console.warn('[Recaptcha] re-render after removal failed:', e2);
					}
					return '';
				}
			},


			/**
			 * Show reCAPTCHA error message
			 */
			showRecaptchaError: function (view, message) {
				var placeholder = view.$('[data-type="alert-placeholder"]');

				if (placeholder.length) {
					placeholder.html(
						'<div class="alert alert-error" role="alert">' +
						'<button type="button" class="close" data-dismiss="alert" aria-label="Close">' +
						'<span aria-hidden="true">&times;</span>' +
						'</button>' +
						message +
						'</div>'
					);
				} else {
					alert(message);
				}
			},

			/**
			 * Reset reCAPTCHA widget (needed after failed form submissions)
			 * Google reCAPTCHA tokens are single-use, so they must be reset after failures
			 */
			resetRecaptcha: function (containerId) {
				try {
					var containerElement = document.getElementById(containerId);
					var widgetId = this.widgetIds[containerId];

					if (typeof window.grecaptcha !== 'undefined' && typeof window.grecaptcha.reset === 'function' && widgetId !== undefined) {
						try {
							window.grecaptcha.reset(widgetId);
							console.log('[Recaptcha] Reset widget', widgetId, 'for', containerId);
							return;
						} catch (e) {
							console.warn('[Recaptcha] Reset failed for', containerId, '-', e);
						}
					}

					console.warn('[Recaptcha] No widget ID found, forcing clean render:', containerId);
					if (containerElement) {
						try { containerElement.innerHTML = ''; } catch (_) {}
						delete this.widgetIds[containerId];
						this.renderRecaptcha(containerId);
					}
				} catch (eOuter) {
					console.warn('[Recaptcha] resetRecaptcha error:', eOuter);
				}
			},
		};
	}
);
